class WnProductForm extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.copy = window.WN_I18N?.product || {};
    this.form = this.querySelector('[data-type="product-form"]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.variantData = this.querySelector('[data-product-variants]');
    if (!this.form || !this.variantInput || !this.variantData) return;

    try { this.variants = JSON.parse(this.variantData.textContent || '[]'); }
    catch { this.variants = []; }

    this.groups = [...this.querySelectorAll('[data-option-position]')];
    this.panels = [...this.querySelectorAll('[data-media-panel]')];
    this.thumbs = [...this.querySelectorAll('[data-media-target]')];
    this.price = this.querySelector('[data-price-current]');
    this.compare = this.querySelector('[data-price-compare]');
    this.stock = this.querySelector('[data-product-availability]');
    this.add = this.querySelector('[data-add-to-cart]');
    this.addText = this.querySelector('[data-add-to-cart-text]');
    this.message = this.querySelector('[data-product-message]');
    this.quantity = this.querySelector('[data-quantity-input]');
    this.mediaStage = this.querySelector('[data-media-stage]');

    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
    document.addEventListener('wn:product:select-variant', (event) => this.onExternalVariantSelection(event), { signal });

    const initial = this.variants.find((variant) => String(variant.id) === String(this.variantInput.value));
    const preserveInitialMedia = this.closest('.wn-nami-recovery') !== null && !new URLSearchParams(window.location.search).has('variant');

    if (preserveInitialMedia && this.panels[0]) this.activateMedia(this.panels[0].dataset.mediaPanel);
    if (initial) this.render(initial, false, preserveInitialMedia);

    if (this.panels.length > 1) this.setupNamiGalleryNavigation(signal);
  }

  disconnectedCallback() { this.abortController?.abort(); this.ready = false; }

  selectedOptions() {
    return this.groups.map((group) => group.querySelector('.is-selected[data-option-value]')?.dataset.optionValue || '');
  }

  findVariant(options) {
    return this.variants.find((variant) => Array.isArray(variant.options) && variant.options.length === options.length && variant.options.every((value, index) => value === options[index]));
  }

  findVariantByTitle(title) {
    const normalized = String(title || '').trim().toLowerCase();
    if (!normalized) return null;
    return this.variants.find((variant) => String(variant.title || '').trim().toLowerCase() === normalized || variant.options?.some((option) => String(option).trim().toLowerCase() === normalized));
  }

  onExternalVariantSelection(event) {
    const variant = this.findVariantByTitle(event.detail?.title);
    if (!variant) return;

    this.groups.forEach((group, index) => {
      const value = variant.options?.[index];
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        const selected = button.dataset.optionValue === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const label = group.querySelector('[data-option-selection]');
      if (label) label.textContent = value || '';
    });

    this.render(variant, true);
    if (event.detail?.scroll === true) this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  onClick(event) {
    const option = event.target.closest('[data-option-value]');
    if (option) {
      const group = option.closest('[data-option-position]');
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        const selected = button === option;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const label = group.querySelector('[data-option-selection]');
      if (label) label.textContent = option.dataset.optionValue || '';
      const variant = this.findVariant(this.selectedOptions());
      variant ? this.render(variant, true) : this.unavailable();
      return;
    }

    const thumb = event.target.closest('[data-media-target]');
    if (thumb) { this.activateMedia(thumb.dataset.mediaTarget); return; }

    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton && this.quantity) {
      const current = Math.max(1, parseInt(this.quantity.value, 10) || 1);
      this.quantity.value = Math.max(1, current + Number(quantityButton.dataset.quantityChange || 0));
    }
  }

  render(variant, updateUrl, preserveMedia = false) {
    this.currentVariant = variant;
    this.variantInput.value = variant.id;
    if (this.price) this.price.textContent = variant.price_formatted || '';

    const onSale = Number(variant.compare_at_price) > Number(variant.price);
    if (this.compare) {
      this.compare.hidden = !onSale;
      this.compare.textContent = onSale ? variant.compare_at_price_formatted || '' : '';
    }

    if (this.add) this.add.disabled = !variant.available;
    if (this.addText) this.addText.textContent = variant.available ? this.add.dataset.addLabel : this.add.dataset.soldOutLabel;
    if (this.stock) {
      this.stock.textContent = variant.available ? (this.copy.available || '') : (this.copy.soldOut || this.add?.dataset.soldOutLabel || '');
      this.stock.dataset.available = String(variant.available);
    }

    if (!preserveMedia && variant.featured_media_id) this.activateMedia(variant.featured_media_id);
    this.markUnavailableOptions();

    if (updateUrl && history.replaceState) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }

    if (this.message) this.message.textContent = '';
    this.dispatchEvent(new CustomEvent('wn:product:variant-change', { bubbles: true, detail: { variant } }));
  }

  unavailable() {
    this.currentVariant = null;
    if (this.add) this.add.disabled = true;
    if (this.addText) this.addText.textContent = this.copy.notAvailable || this.add?.dataset.soldOutLabel || '';
    if (this.stock) {
      this.stock.textContent = this.copy.combinationUnavailable || this.copy.notAvailable || '';
      this.stock.dataset.available = 'false';
    }
  }

  markUnavailableOptions() {
    const selected = this.selectedOptions();
    this.groups.forEach((group, index) => {
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        const options = [...selected];
        options[index] = button.dataset.optionValue || '';
        const variant = this.findVariant(options);
        button.classList.toggle('is-sold-out', Boolean(variant && !variant.available));
      });
    });
  }

  activateMedia(mediaId) {
    const id = String(mediaId || '');
    const target = this.panels.find((panel) => String(panel.dataset.mediaPanel) === id);
    if (!target) return;
    const current = this.panels.find((panel) => panel.classList.contains('is-active') && !panel.hidden) || null;

    const request = new CustomEvent('wn:product:media-change-request', {
      bubbles: false,
      cancelable: true,
      detail: { mediaId: id, current, target }
    });
    if (!this.dispatchEvent(request)) return;
    this.commitMediaActivation(id);
  }

  commitMediaActivation(mediaId) {
    const id = String(mediaId || '');
    const active = this.panels.find((panel) => String(panel.dataset.mediaPanel) === id);
    if (!active) return;

    const current = this.panels.find((panel) => panel.classList.contains('is-active') && !panel.hidden);
    if (current && current !== active) this.resetNamiGalleryZoom();

    this.panels.forEach((panel) => {
      const selected = panel === active;
      panel.hidden = !selected;
      panel.classList.toggle('is-active', selected);
      panel.setAttribute('aria-hidden', String(!selected));
      if (!selected) panel.style.removeProperty('transform');
    });

    this.thumbs.forEach((thumb) => {
      const selected = String(thumb.dataset.mediaTarget) === id;
      thumb.classList.toggle('is-active', selected);
      thumb.setAttribute('aria-current', String(selected));
    });

    this.updateNamiGalleryNavigation();
    this.dispatchEvent(new CustomEvent('wn:product:media-changed', { detail: { mediaId: id, panel: active } }));
  }

  setupNamiGalleryNavigation(signal) {
    if (!this.mediaStage || this.panels.length < 2 || this.mediaStage.dataset.wnGalleryEnhanced === 'true') return;
    this.mediaStage.dataset.wnGalleryEnhanced = 'true';

    this.namiZoomScale = 1;
    this.namiZoomX = 0;
    this.namiZoomY = 0;
    this.namiZoomPointers = new Map();
    this.namiSuppressImageClick = false;
    this.namiGalleryPointer = null;
    this.namiPinchStart = null;

    this.mediaStage.setAttribute('role', 'region');
    this.mediaStage.setAttribute('aria-label', this.copy.images || 'Product images');
    if (!this.mediaStage.hasAttribute('tabindex')) this.mediaStage.tabIndex = 0;

    const previous = document.createElement('button');
    previous.type = 'button';
    previous.className = 'wn-nami-gallery-nav wn-nami-gallery-nav--previous';
    previous.dataset.namiGalleryPrevious = '';
    previous.setAttribute('aria-label', this.copy.previousImage || 'Previous image');
    previous.innerHTML = '<span aria-hidden="true">‹</span>';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'wn-nami-gallery-nav wn-nami-gallery-nav--next';
    next.dataset.namiGalleryNext = '';
    next.setAttribute('aria-label', this.copy.nextImage || 'Next image');
    next.innerHTML = '<span aria-hidden="true">›</span>';

    const zoom = document.createElement('button');
    zoom.type = 'button';
    zoom.className = 'wn-nami-gallery-zoom';
    zoom.dataset.namiGalleryZoom = '';
    zoom.setAttribute('aria-label', this.copy.zoomImage || 'Zoom image');
    zoom.setAttribute('aria-pressed', 'false');
    zoom.innerHTML = this.namiGalleryZoomIcon(false);

    const counter = document.createElement('div');
    counter.className = 'wn-nami-gallery-counter';
    counter.dataset.namiGalleryCounter = '';
    counter.setAttribute('aria-live', 'polite');
    counter.setAttribute('aria-atomic', 'true');

    this.mediaStage.append(previous, next, zoom, counter);
    this.namiGalleryPrevious = previous;
    this.namiGalleryNext = next;
    this.namiGalleryZoom = zoom;
    this.namiGalleryCounter = counter;

    previous.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); if (!this.isNamiGalleryZoomed()) this.stepNamiGallery(-1); }, { signal });
    next.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); if (!this.isNamiGalleryZoomed()) this.stepNamiGallery(1); }, { signal });
    zoom.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); this.toggleNamiGalleryZoom(); }, { signal });

    this.mediaStage.addEventListener('click', (event) => {
      if (event.target.closest('.wn-nami-gallery-nav, .wn-nami-gallery-zoom')) return;
      if (!event.target.closest('.wn-product-gallery__image')) return;
      if (this.namiSuppressImageClick) { this.namiSuppressImageClick = false; return; }
      this.toggleNamiGalleryZoom();
    }, { signal });

    this.mediaStage.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isNamiGalleryZoomed()) { event.preventDefault(); this.resetNamiGalleryZoom(); return; }
      if (this.isNamiGalleryZoomed()) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.stepNamiGallery(-1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); this.stepNamiGallery(1); }
    }, { signal });

    this.mediaStage.addEventListener('pointerdown', (event) => this.onNamiGalleryPointerDown(event), { signal });
    this.mediaStage.addEventListener('pointermove', (event) => this.onNamiGalleryPointerMove(event), { signal });
    this.mediaStage.addEventListener('pointerup', (event) => this.onNamiGalleryPointerUp(event), { signal });
    this.mediaStage.addEventListener('pointercancel', (event) => this.onNamiGalleryPointerCancel(event), { signal });

    let wheelLocked = false;
    this.mediaStage.addEventListener('wheel', (event) => {
      if (this.isNamiGalleryZoomed()) return;
      const horizontalIntent = Math.abs(event.deltaX) > Math.abs(event.deltaY) * 1.25 && Math.abs(event.deltaX) > 18;
      if (!horizontalIntent) return;
      const currentIndex = this.activeNamiGalleryIndex();
      const delta = event.deltaX > 0 ? 1 : -1;
      const nextIndex = currentIndex + delta;
      if (nextIndex < 0 || nextIndex >= this.panels.length) return;
      event.preventDefault();
      if (wheelLocked) return;
      wheelLocked = true;
      this.stepNamiGallery(delta);
      window.setTimeout(() => { wheelLocked = false; }, 320);
    }, { passive: false, signal });

    this.updateNamiGalleryNavigation();
  }

  namiGalleryZoomIcon(zoomed) {
    return zoomed
      ? '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"></circle><path d="m15.5 15.5 4.5 4.5M7.5 10.5h6"></path></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"></circle><path d="m15.5 15.5 4.5 4.5M10.5 7.5v6M7.5 10.5h6"></path></svg>';
  }

  activeNamiGalleryIndex() {
    const index = this.panels.findIndex((panel) => panel.classList.contains('is-active') && !panel.hidden);
    return index >= 0 ? index : 0;
  }

  activeNamiGalleryPanel() { return this.panels[this.activeNamiGalleryIndex()] || null; }
  activeNamiGalleryImage() { return this.activeNamiGalleryPanel()?.querySelector('.wn-product-gallery__image') || null; }
  isNamiGalleryZoomed() { return Number(this.namiZoomScale || 1) > 1.01; }

  stepNamiGallery(delta) {
    if (!this.panels.length || this.isNamiGalleryZoomed()) return;
    const currentIndex = this.activeNamiGalleryIndex();
    const nextIndex = Math.min(this.panels.length - 1, Math.max(0, currentIndex + delta));
    if (nextIndex === currentIndex) return;
    this.activateMedia(this.panels[nextIndex].dataset.mediaPanel);
  }

  toggleNamiGalleryZoom() { this.isNamiGalleryZoomed() ? this.resetNamiGalleryZoom() : this.setNamiGalleryZoom(2, 0, 0); }

  setNamiGalleryZoom(scale, x = this.namiZoomX || 0, y = this.namiZoomY || 0) {
    const nextScale = Math.min(3, Math.max(1, Number(scale) || 1));
    this.namiZoomScale = nextScale;
    this.namiZoomX = Number(x) || 0;
    this.namiZoomY = Number(y) || 0;
    if (nextScale <= 1.01) { this.namiZoomScale = 1; this.namiZoomX = 0; this.namiZoomY = 0; }
    else this.clampNamiGalleryPan();
    this.applyNamiGalleryZoom();
  }

  clampNamiGalleryPan() {
    if (!this.mediaStage || !this.isNamiGalleryZoomed()) { this.namiZoomX = 0; this.namiZoomY = 0; return; }
    const rect = this.mediaStage.getBoundingClientRect();
    const maxX = Math.max(0, (rect.width * (this.namiZoomScale - 1)) / 2);
    const maxY = Math.max(0, (rect.height * (this.namiZoomScale - 1)) / 2);
    this.namiZoomX = Math.max(-maxX, Math.min(maxX, this.namiZoomX));
    this.namiZoomY = Math.max(-maxY, Math.min(maxY, this.namiZoomY));
  }

  applyNamiGalleryZoom() {
    const image = this.activeNamiGalleryImage();
    const zoomed = this.isNamiGalleryZoomed();
    this.mediaStage?.classList.toggle('is-zoomed', zoomed);
    if (image) image.style.transform = zoomed ? `translate3d(${this.namiZoomX}px, ${this.namiZoomY}px, 0) scale(${this.namiZoomScale})` : '';
    if (this.namiGalleryZoom) {
      this.namiGalleryZoom.setAttribute('aria-pressed', String(zoomed));
      this.namiGalleryZoom.setAttribute('aria-label', zoomed ? (this.copy.zoomOut || 'Zoom out') : (this.copy.zoomImage || 'Zoom image'));
      this.namiGalleryZoom.innerHTML = this.namiGalleryZoomIcon(zoomed);
    }
    this.updateNamiGalleryNavigation();
  }

  resetNamiGalleryZoom() {
    this.namiZoomScale = 1;
    this.namiZoomX = 0;
    this.namiZoomY = 0;
    this.namiZoomPointers?.clear();
    this.namiPinchStart = null;
    this.namiGalleryPointer = null;
    this.mediaStage?.classList.remove('is-zoomed', 'is-zoom-panning', 'is-dragging');
    this.panels.forEach((panel) => {
      panel.style.removeProperty('transform');
      panel.querySelector('.wn-product-gallery__image')?.style.removeProperty('transform');
    });
    if (this.namiGalleryZoom) {
      this.namiGalleryZoom.setAttribute('aria-pressed', 'false');
      this.namiGalleryZoom.setAttribute('aria-label', this.copy.zoomImage || 'Zoom image');
      this.namiGalleryZoom.innerHTML = this.namiGalleryZoomIcon(false);
    }
    this.updateNamiGalleryNavigation();
  }

  onNamiGalleryPointerDown(event) {
    if (!this.mediaStage || event.target.closest('.wn-nami-gallery-nav, .wn-nami-gallery-zoom')) return;
    if (!event.target.closest('.wn-product-gallery__image')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    try { this.mediaStage.setPointerCapture(event.pointerId); } catch {}

    if (this.isNamiGalleryZoomed()) {
      event.preventDefault();
      this.namiZoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.mediaStage.classList.add('is-zoom-panning');
      if (this.namiZoomPointers.size === 1) {
        this.namiGalleryPointer = { id: event.pointerId, startX: event.clientX, startY: event.clientY, originX: this.namiZoomX, originY: this.namiZoomY, moved: false };
        this.namiPinchStart = null;
      } else if (this.namiZoomPointers.size === 2) {
        const points = [...this.namiZoomPointers.values()];
        this.namiPinchStart = { distance: Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y)), scale: this.namiZoomScale };
        this.namiGalleryPointer = null;
      }
      return;
    }

    if (!event.isPrimary) return;
    this.namiGalleryPointer = { id: event.pointerId, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, moved: false };
  }

  onNamiGalleryPointerMove(event) {
    if (!this.namiGalleryPointer && !this.namiZoomPointers?.has(event.pointerId)) return;

    if (this.isNamiGalleryZoomed()) {
      if (!this.namiZoomPointers.has(event.pointerId)) return;
      event.preventDefault();
      this.namiZoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.namiZoomPointers.size >= 2) {
        const points = [...this.namiZoomPointers.values()].slice(0, 2);
        const distance = Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y));
        if (!this.namiPinchStart) this.namiPinchStart = { distance, scale: this.namiZoomScale };
        this.namiSuppressImageClick = true;
        this.setNamiGalleryZoom(this.namiPinchStart.scale * (distance / this.namiPinchStart.distance), this.namiZoomX, this.namiZoomY);
        return;
      }
      if (this.namiGalleryPointer?.id === event.pointerId) {
        const deltaX = event.clientX - this.namiGalleryPointer.startX;
        const deltaY = event.clientY - this.namiGalleryPointer.startY;
        if (Math.hypot(deltaX, deltaY) > 4) { this.namiGalleryPointer.moved = true; this.namiSuppressImageClick = true; }
        this.namiZoomX = this.namiGalleryPointer.originX + deltaX;
        this.namiZoomY = this.namiGalleryPointer.originY + deltaY;
        this.clampNamiGalleryPan();
        this.applyNamiGalleryZoom();
      }
      return;
    }

    if (this.namiGalleryPointer?.id !== event.pointerId) return;
    this.namiGalleryPointer.x = event.clientX;
    this.namiGalleryPointer.y = event.clientY;
    const deltaX = event.clientX - this.namiGalleryPointer.startX;
    const deltaY = event.clientY - this.namiGalleryPointer.startY;
    if (Math.abs(deltaX) > 5 && Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      this.namiGalleryPointer.moved = true;
      this.namiSuppressImageClick = true;
      this.mediaStage.classList.add('is-dragging');
      const panel = this.activeNamiGalleryPanel();
      if (panel) panel.style.transform = `translate3d(${deltaX * 0.28}px, 0, 0)`;
    }
  }

  onNamiGalleryPointerUp(event) {
    try { if (this.mediaStage?.hasPointerCapture(event.pointerId)) this.mediaStage.releasePointerCapture(event.pointerId); } catch {}

    if (this.isNamiGalleryZoomed()) {
      const pointer = this.namiGalleryPointer;
      const wasPinching = this.namiZoomPointers.size > 1;
      this.namiZoomPointers.delete(event.pointerId);
      if (this.namiZoomScale <= 1.05) { this.namiSuppressImageClick = true; this.resetNamiGalleryZoom(); window.setTimeout(() => { this.namiSuppressImageClick = false; }, 0); return; }
      if (!wasPinching && pointer?.id === event.pointerId && !pointer.moved && this.namiZoomPointers.size === 0) { this.namiSuppressImageClick = true; this.resetNamiGalleryZoom(); window.setTimeout(() => { this.namiSuppressImageClick = false; }, 0); return; }
      if (wasPinching) { this.namiSuppressImageClick = true; this.namiPinchStart = null; window.setTimeout(() => { this.namiSuppressImageClick = false; }, 0); }
      else if (pointer?.moved) window.setTimeout(() => { this.namiSuppressImageClick = false; }, 0);
      if (this.namiZoomPointers.size === 1) {
        const [id, point] = this.namiZoomPointers.entries().next().value;
        this.namiGalleryPointer = { id, startX: point.x, startY: point.y, originX: this.namiZoomX, originY: this.namiZoomY, moved: wasPinching };
      } else { this.namiGalleryPointer = null; this.mediaStage.classList.remove('is-zoom-panning'); }
      return;
    }

    if (!this.namiGalleryPointer || event.pointerId !== this.namiGalleryPointer.id) return;
    const pointer = this.namiGalleryPointer;
    this.namiGalleryPointer = null;
    const panel = this.activeNamiGalleryPanel();
    if (panel) panel.style.removeProperty('transform');
    this.mediaStage.classList.remove('is-dragging');
    const deltaX = event.clientX - pointer.startX;
    const deltaY = event.clientY - pointer.startY;
    if (!pointer.moved || Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
    event.preventDefault();
    this.stepNamiGallery(deltaX < 0 ? 1 : -1);
    window.setTimeout(() => { this.namiSuppressImageClick = false; }, 0);
  }

  onNamiGalleryPointerCancel(event) {
    this.namiZoomPointers?.delete(event.pointerId);
    if (this.namiGalleryPointer?.id === event.pointerId) this.namiGalleryPointer = null;
    this.namiPinchStart = null;
    this.mediaStage?.classList.remove('is-dragging', 'is-zoom-panning');
    this.activeNamiGalleryPanel()?.style.removeProperty('transform');
  }

  updateNamiGalleryNavigation() {
    if (!this.namiGalleryPrevious || !this.namiGalleryNext || !this.namiGalleryCounter) return;
    const currentIndex = this.activeNamiGalleryIndex();
    const zoomed = this.isNamiGalleryZoomed();
    this.namiGalleryPrevious.disabled = zoomed || currentIndex <= 0;
    this.namiGalleryNext.disabled = zoomed || currentIndex >= this.panels.length - 1;
    this.namiGalleryCounter.textContent = `${currentIndex + 1} / ${this.panels.length}`;
    const activeThumb = this.thumbs.find((thumb) => thumb.classList.contains('is-active'));
    if (!zoomed) activeThumb?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  loading(state) {
    if (!this.add || !this.addText) return;
    this.add.disabled = state || !this.currentVariant?.available;
    this.add.setAttribute('aria-busy', String(state));
    this.addText.textContent = state ? this.add.dataset.loadingLabel : this.currentVariant?.available ? this.add.dataset.addLabel : this.add.dataset.soldOutLabel;
  }

  async onSubmit(event) {
    if (!window.WNCart?.add) return;
    event.preventDefault();
    if (!this.currentVariant?.available) return;

    const variant = this.currentVariant;
    this.loading(true);
    if (this.message) { this.message.className = 'wn-product-form__message'; this.message.textContent = ''; }
    this.dispatchEvent(new CustomEvent('wn:product:add-start', { bubbles: true, detail: { variant } }));

    try {
      const { cart, item } = await window.WNCart.add(new FormData(this.form), { source: 'product-form', openDrawer: true });
      if (this.message) { this.message.className = 'wn-product-form__message is-success'; this.message.textContent = this.copy.addedToCart || ''; }
      this.dispatchEvent(new CustomEvent('wn:product:add-success', { bubbles: true, detail: { cart, item, variant } }));
    } catch (error) {
      const message = error.message || this.copy.addError || '';
      if (this.message) { this.message.className = 'wn-product-form__message is-error'; this.message.textContent = message; }
      this.dispatchEvent(new CustomEvent('wn:product:add-error', { bubbles: true, detail: { error, variant } }));
    } finally { this.loading(false); }
  }
}

if (!customElements.get('wn-product-form')) customElements.define('wn-product-form', WnProductForm);
